import React, { useCallback, useMemo, useState } from "react";

import { ApiJob, ApiJobContent } from "@common/api/v1.1/types/jobs";
import { ChildrenArgs } from "@common/components/Workbench/types";
import { Zoning } from "@zoning/components/Zoning";
import { Size } from "@zoning/components/Zoning/Renderer/types";
import {
  ApiZone,
  JobState,
  SavePage,
  SubmitJob,
  ZoningTaxonomy,
} from "@zoning/components/Zoning/types";
import { addTaxonomyColorsAndHotkeys } from "./taxonomy";

export type TaxonomyContent = ApiJobContent & {
  contents: ZoningTaxonomy;
  role: "zoning-taxonomy-json";
};

type Props = Readonly<{
  autoShrink: boolean;
  checkinJob: ChildrenArgs["actions"]["checkinJob"];
  contents: Array<ApiJobContent>;
  initialZones: Array<Array<ApiZone>>;
  job: ApiJob;
  noteCount: number;
  notes: React.ReactElement;
  pageConfidences: Array<number>;
  pageDimensions: Array<Size>;
  readonly: boolean;
  resetToZones: Array<Array<ApiZone>> | undefined;
  saveContent: ChildrenArgs["actions"]["saveContent"];
  token: string;
  taxonomy: TaxonomyContent | undefined;
}>;

export const ZoningWrapper = ({
  autoShrink,
  checkinJob,
  contents,
  initialZones,
  job,
  noteCount,
  notes,
  pageConfidences,
  pageDimensions,
  readonly,
  resetToZones,
  saveContent,
  taxonomy,
  token,
}: Props) => {
  const [jobState, setJobState] = useState(JobState.Default);
  const lowResImageURIs = useMemo(
    () =>
    {
      let uris = contents
        .filter(c => c.role === "zoning-page-png")
        .map(c => {
          if (c.requires_auth) {
            return `${c.uri}?token=${encodeURIComponent(token)}`;
          }
          return c.uri;
        });
        if (job.parent_id) {
          uris = uris.slice(job.metadata.first_page -1 , job.metadata.last_page -1)
        }
        return uris;
    },
    [contents, token],
  );
  const highResImageURIs = useMemo(
    () =>
    {
      let uris = contents
        .filter(c => c.role === "zoning-pagehr-png")
        .map(c => {
          if (c.requires_auth) {
            return `${c.uri}?token=${encodeURIComponent(token)}`;
          }
          return c.uri;
        });
        if (job.parent_id) {
          uris = uris.slice(job.metadata.first_page -1, job.metadata.last_page -1)
        }
        return uris;
    },
    [contents, token],
  );

  const savePage: SavePage = useCallback(
    async (page, content, showMessage) => {
      setJobState(JobState.Saving);
      const isNew = !contents.some(c => c.role === "zoning-reviewed-json" && c.order === page);
      try {
        await saveContent(
          "zoning-reviewed-json",
          page,
          JSON.stringify(content),
          isNew,
          "application/json",
          undefined,
          showMessage,
        );
        setJobState(JobState.Default);
      } catch (err) {
        setJobState(JobState.Default);
        throw err;
      }
    },
    [contents, saveContent, setJobState],
  );
  const submitJob: SubmitJob = useCallback(async () => {
    setJobState(JobState.Submitting);
    try {
      await checkinJob();
    } catch (err) {
      setJobState(JobState.Default);
    }
  }, [checkinJob, setJobState]);
  const zoningTaxonomy: ZoningTaxonomy = useMemo(() => {
    if (taxonomy && taxonomy.contents) {
      const taxonomyContents = taxonomy.contents;
      if (typeof taxonomyContents === "string") {
        contents = JSON.parse(taxonomyContents);
      }
      return addTaxonomyColorsAndHotkeys(taxonomyContents);
    }
    return addTaxonomyColorsAndHotkeys(undefined);
  }, [taxonomy]);
  return (
    <Zoning
      autoShrink={autoShrink}
      highConfidenceThreshold={Number.parseFloat(job.metadata.high_confidence_threshold) || 1.0}
      zoningTaxonomyName={job.metadata.zoning.taxonomy}
      highResImageURIs={highResImageURIs}
      initialZones={initialZones}
      jobState={jobState}
      lowResImageURIs={lowResImageURIs}
      noteCount={noteCount}
      notes={notes}
      pageConfidences={pageConfidences}
      pageDimensions={pageDimensions}
      readonly={readonly}
      resetToZones={resetToZones}
      savePage={savePage}
      submitJob={submitJob}
      taxonomy={zoningTaxonomy}
    />
  );
};
ZoningWrapper.displayName = "ZoningWrapper";
